import { COUNTRY_CODES, type CountryCode, type Locale } from '@aysar/contracts';

import { type SelectionOption } from '../types/selection-option.types';

/** A catalog lookup for one code, or `undefined`/`''` when the catalog is silent. */
export type CountryNameOverride = (code: CountryCode) => string | undefined;

/**
 * The name of a country, in one locale.
 *
 * **The names come from `Intl`**, which knows all 249 of them in both locales —
 * the same argument `MoneyInput` makes for currency names. Hand-writing them
 * would be 498 strings whose only job is to restate CLDR, and whose maintenance
 * cost is paid every time a country is renamed.
 *
 * **The `country.<CODE>` catalog key stays the override**, exactly as
 * `countryLabelKey` in `@aysar/contracts` documents: a deployment that disagrees
 * with CLDR's rendering of a name adds one key and changes no code. That is the
 * part U8 is actually protecting — the ability to translate what is on screen —
 * and it costs nothing to keep it while the default is free.
 *
 * A runtime whose ICU data was trimmed answers with the code itself or with
 * nothing; both fall back to the **code**, which is honest, sorts predictably,
 * and is the value being stored anyway.
 */
export function countryName(
  code: CountryCode,
  locale: Locale,
  overrideFor?: CountryNameOverride,
): string {
  const override = overrideFor?.(code);
  if (override !== undefined && override.trim() !== '') return override;

  const displayed = displayNamesFor(locale).of(code);

  return displayed === undefined || displayed.trim() === '' ? code : displayed;
}

/**
 * Every ISO 3166-1 alpha-2 country, as rows a selection control can render.
 *
 * The **code is the description**, which is not decoration: the kit's default
 * filter searches `label` and `description`, so typing `SY` finds Syria without
 * a bespoke `filterFn` — one definition of "matches" rather than two. It also
 * puts the value that will actually be stored on the row the operator picks.
 *
 * Sorted with the locale's own collator: `.sort()` compares UTF-16 code units,
 * which puts Arabic names in an order no Arabic reader recognises.
 *
 * Deliberately **not** filtered to the markets a brand serves (U16) — that is a
 * settings question, and a member's country may legitimately be anywhere.
 */
export function countryOptions(
  locale: Locale,
  overrideFor?: CountryNameOverride,
): SelectionOption[] {
  const collator = new Intl.Collator(locale);

  return COUNTRY_CODES.map((code) => ({
    value: code,
    label: countryName(code, locale, overrideFor),
    description: code,
  })).sort((first, second) => collator.compare(first.label, second.label));
}

/**
 * Constructed per call rather than cached in a module-scope map: `Intl` caches
 * its own formatters internally, and a module-scope cache would outlive a test
 * that stubs `Intl.DisplayNames` — which is precisely how the no-ICU fallback
 * stops being provable.
 */
function displayNamesFor(locale: Locale): Intl.DisplayNames {
  return new Intl.DisplayNames([locale], { type: 'region' });
}
